import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FlowManagerService } from '../../../services/flow-manager.service';
import { Router } from '@angular/router';

import {Observable, Subscription} from 'rxjs';
import { FlowUsageResponse } from '../../../../../shared/models/FlowUsage';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import { SharedService } from '../../../../../core/services/store/shared-store/shared-storage.service';
import { SubmissionService } from '../../../../../core/services/management/submission/submission.service';
import { SubmissionRequest } from '../../../../../shared/models/flow-manager-core';
import {ToastBannerService} from '../../../../../shared/components/cms/toast-banner/toast-banner.service';
import {select, Store} from '@ngrx/store';
import {getEntityConfig} from '../../../../../core/state/selectors/entity-config.selector';
import {loadEntityConfig} from '../../../../../core/state/actions/entity-config.action';
import {map} from 'rxjs/internal/operators';
import { FlowIssueItem } from '../../delete-flow/flow-issues-table/flow-issues-table.component';


const USAGE_ENV = ['sandbox', 'production'];
type DeletionState = 'flowUsage' | 'deletionRequest' | 'deleteFlow' | 'errorDeletingFlow';

@Component({
    selector: 'app-delete-bot-modal',
    templateUrl: './delete-bot-modal.component.html',
    styleUrls: ['./delete-bot-modal.component.scss'],
})
export class DeleteBotModalComponent implements OnInit, OnDestroy {
    tempEntityType: string = this.sharedService.getEntityType();
    tempFlowid: any = this.sharedService.getEntityId();
    flowId: any;
    newFlowName: string;
    requestFlowLoad: string;
    requestRouteLoad: string;
    savingInProgress: Boolean = true;
    deletionReason: String = '';

    public flowInUse = false;
    public issues: FlowIssueItem[];

    private mySubscriptions: Subscription[] = [];
    private flowUsage: FlowUsageResponse;

    public flowData: any;
    public deletionState: DeletionState;
    public deletingFlowInProgress = false;
    public approveDeletingFlowInProgress = false;
    public entityConfig$: Observable<any>;
    public isSingleApprover = false;

    constructor(
        public dialogRef: MatDialogRef<DeleteBotModalComponent>,
        private router: Router,
        private flowManagerService: FlowManagerService,
        private sharedService: SharedService,
        private toastService: ToastBannerService,
        private submissionService: SubmissionService,
        private store: Store
    ) {}

    ngOnInit() {
        this.flowId = this.dialogRef._containerInstance._config.data.flowId;
        this.newFlowName = this.dialogRef._containerInstance._config.data.flowName;
        this.store.dispatch(loadEntityConfig());
        this.entityConfig$ = this.store.pipe(select(getEntityConfig));
        this.entityConfig$.pipe(map(state => state)).subscribe((currentState) => this.isSingleApprover = currentState['single-approver']);

        const FlowMetaDataSubscription: Subscription = this.flowManagerService
            .getFlowMetaDataById(this.flowId)
            .subscribe((flow) => {
                if (flow) {
                    this.flowData = flow;
                }
            });
        this.mySubscriptions.push(FlowMetaDataSubscription);

        const FlowUsagesSubscription: Subscription = this.flowManagerService
            .getFlowUsages(this.flowId)
            .subscribe((usage) => {
                this.savingInProgress = false;
                this.flowUsage = usage;
                this.deletionState = this.getDeletionState(usage);
                this.flowInUse = this.isFlowInUse(usage);
                this.issues = this.toFlowIssueItem(usage);
            });

        this.mySubscriptions.push(FlowUsagesSubscription);
    }

    private getDeletionState(usage: FlowUsageResponse): DeletionState {
        if (
            this.flowData &&
            this.flowData.hasOwnProperty('flowStatus') &&
            !this.sharedService.canDeleteFlow(this.flowData.flowStatus)
        ) {
            return 'errorDeletingFlow';
        }
        if (this.isFlowInUse(usage)) {
            return 'flowUsage';
        }
        if (this.isSingleApprover) {
            return 'deleteFlow';
        }
        if (this.flowUsage.isFlowInProd) {
            return 'deletionRequest';
        }
        return 'deleteFlow';
    }

    ngOnDestroy(): void {
        for (const subs of this.mySubscriptions) {
            subs.unsubscribe();
        }
    }

    saveFlow() {
        // this.flowManagerService.getFlows(true).subscribe((flows) => {
        //     this.sharedService.updateFlows(flows);
        // });
        this.closeModal();
    }

    closeModal() {
        this.dialogRef.close();
    }

    goTo(issue: FlowIssueItem) {
        this.closeModal();
        localStorage.setItem('flowIssue', JSON.stringify(issue));
        switch (issue.type) {
            case 'flow':
                this.router.navigate([
                    'flow',
                    this.sharedService.getEnvironment(),
                    'CLIENT',
                    this.sharedService.getEntityId(),
                    issue.src,
                ]);
                break;
            case 'route':
                this.router.navigate(['/routing']);
                break;
            case 'keyword':
                this.router.navigate(['/keywords']);
        }
    }

    public deleteFlowClick() {
        this.deletingFlowInProgress = true;
        this.flowManagerService.deleteFlow(this.flowId).subscribe(
            (response) => {
                this.deletingFlowInProgress = false;
                if (!response['result']) {
                    this.deletionState = 'errorDeletingFlow';
                } else {
                    this.closeModal();
                    this.flowManagerService.getFlows(true).subscribe((flows) => {
                        this.sharedService.updateFlows(flows);
                    });
                    this.router.navigate(['deletion/success/', this.newFlowName]);
                }
            },
            (response) => {
                if (!response.result) {
                    this.displayBanner(
                        '#c23313',
                        'delete_thrash_50.svg',
                        `Request to delete flow ${this.newFlowName} wasn't sent.`,
                        `Flow ${this.newFlowName}  cannot be deleted because it is currently awaiting deploying.Once it has been approved by an admin it will no longer appear in the left bar.`,
                        true,
                        true
                    );
                    this.closeModal();
                }
            }
        );
    }

    public submitForDeletion(summary: any) {
        this.approveDeletingFlowInProgress = true;
        const submissionRequest: SubmissionRequest = {
            summary: summary,
            fileName: this.flowId,
            // * might be worth it to use this.flowName as opposed to the shared service
            flowPath: this.newFlowName,
            entityId: this.sharedService.getEntityId(),
            entityType: 'CLIENT',
            submissionType: 'DELETE',
        };

        this.submissionService
            .createSubmission(this.sharedService.getEnvironment(), submissionRequest)
            .subscribe((response) => {
                this.approveDeletingFlowInProgress = false;
                if (response['result']) {
                    this.displayBanner(
                        '#c23313',
                        'delete_thrash_50.svg',
                        'Request to delete flow “' + this.newFlowName + '” successfully sent.',
                        'Once it has been approved by an admin it will no longer appear in the left bar.',
                        true,
                        true
                    );
                    this.closeModal();
                } else {
                    console.warn(response['message']);
                }
                this.flowManagerService.getFlows(true).subscribe((flows) => {
                    this.sharedService.updateFlows(flows);
                });
            });
    }

    private isFlowInUse(usage: FlowUsageResponse): boolean {
        for (const usageEnv of USAGE_ENV) {
            if (!isEmpty(usage[usageEnv].flows)) {
                return true;
            }
            if (!isEmpty(usage[usageEnv].keywords)) {
                return true;
            }
            if (!isEmpty(usage[usageEnv].routes)) {
                return true;
            }
        }

        return false;
    }

    private toFlowIssueItem(usage: FlowUsageResponse): FlowIssueItem[] {
        let index = 0;
        const issueList: FlowIssueItem[] = [];
        for (const usageEnv of USAGE_ENV) {
            if (!isEmpty(usage[usageEnv].flows)) {
                for (const flow of usage[usageEnv].flows) {
                    for (const step of flow.flowSteps) {
                        index += 1;
                        const issue: FlowIssueItem = {
                            id: index,
                            location: flow.flowPath,
                            environment: usageEnv.toUpperCase(),
                            channel: '-',
                            description: step,
                            action: 'Go to step',
                            src: flow.flowId,
                            type: 'flow',
                            step: step,
                        };
                        issueList.push(issue);
                    }
                }
            }

            if (!isEmpty(usage[usageEnv].keywords)) {
                for (const channel in usage[usageEnv].keywords) {
                    index += 1;
                    const issue: FlowIssueItem = {
                        id: index,
                        location: 'Keywords',
                        environment: usageEnv.toUpperCase(),
                        channel: channel,
                        description: `${usage[usageEnv].keywords[channel]} Keyword Group(s) `,
                        action: 'Go to Keywords',
                        src: '',
                        type: 'keyword',
                    };
                    issueList.push(issue);
                }
            }

            if (!isEmpty(usage[usageEnv].routes)) {
                for (const channel in usage[usageEnv].routes) {
                    for (const route in usage[usageEnv].routes[channel]) {
                        index += 1;
                        const issue: FlowIssueItem = {
                            id: index,
                            location: 'Routing',
                            environment: usageEnv.toUpperCase(),
                            channel: channel,
                            description: usage[usageEnv].routes[channel][route],
                            action: 'Go to Routing',
                            src: '',
                            type: 'route',
                        };
                        issueList.push(issue);
                    }
                }
            }
        }
        return issueList;
    }

    setIssuesDesc() {
        const flowIssuesDesc = {
            flowName: this.newFlowName,
            inProd: this.flowUsage && this.flowUsage.isFlowInProd ? this.flowUsage.isFlowInProd : false,
            types: '',
        };
        const issues = [];

        const flow = find(this.issues, { type: 'flow' });
        const keyword = find(this.issues, { type: 'keyword' });
        const route = find(this.issues, { type: 'route' });

        if (flow) {
            issues.push('other flows');
        }
        if (route) {
            issues.push('routes');
        }
        if (keyword) {
            issues.push('keywords');
        }

        if (issues.length > 0) {
            flowIssuesDesc.types =
                'is linked to either ' + issues.join(', ').replace(/,([^,]*)$/, ' and' + '$1');
        }

        return `Flow <span class="bold-text">${flowIssuesDesc.flowName}</span>
            ${
            flowIssuesDesc.inProd
                ? 'is currently <span class="bold-text">Deployed to Production</span>'
                : ''
        }
            ${flowIssuesDesc.inProd && flowIssuesDesc.types ? 'and' : ''}
            <span class="bold-text">${flowIssuesDesc.types}</span>.
            Please fix and disconnect these links in order to delete the flow.`;
    }

    getDateFormat(createdTimestamp) {
        if (createdTimestamp) {
            return createdTimestamp.replace(/-/g, '/');
        }
        return 'N/A';
    }

    displayBanner(
        lineColor: string,
        icon: string,
        title: string,
        desc: string,
        bigIcon: boolean,
        closeIcon: boolean
    ): void {
        this.toastService.setBannerParams({ lineColor, icon, title, desc, bigIcon, closeIcon });
        setTimeout(() => {
            this.toastService.clearBannerParams();
        }, 5000);
    }
}
